import React, { useCallback, useMemo, useState } from 'react';
import classes from './style.module.scss';
import Modal from '~/app/common/Modal';
import { useStoreActions, useStoreState } from '~/store';
import MaterialForm from './MaterialForm';
import Checkout from './Checkout';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import { Material } from '~/types';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import ConfirmationMenu from '~/app/common/ConfirmationMenu';
import TextDialog from '~/app/common/TextDialog';
import Quantity from '~/app/common/Quantity';
import {
  formatMaterialPrice,
  getMaterialStoreKey,
  isTempMaterialsDirty,
  isVariousMaterial,
} from '../helper';
import MaterialRowMenu from '../MaterialRowMenu';
import QuantityEditForm from '../QuantityEditForm';

export interface AddMaterialDialogProps {
  open: boolean
  onClose: () => void
  type: PartLinkTypes
}

export type AddMaterialDialogComponent = React.FC<AddMaterialDialogProps>;

const AddMaterialDialog: AddMaterialDialogComponent = ({ open, onClose, type }) => {
  const basePath = getMaterialStoreKey(type);
  const materials = useStoreState(state => state[basePath].data.materials ?? []);
  const tempMaterials = useStoreState(state => state[basePath].tempMaterials ?? []);
  const setTempMaterialsAction = useStoreActions(actions => actions[basePath].setTempMaterials);

  const [isOpenCheckoutPage, setIsOpenCheckoutPage] = useState(false);
  const [isOpenMaterialForm, setIsOpenMaterialForm] = useState(false);
  const [isOpenQuantityEdit, setIsOpenQuantityEdit] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const [isVariousBlockOpen, setIsVariousBlockOpen] = useState(false);
  const [materialForEdit, setMaterialForEdit] = useState<Material>();

  const { t } = useTranslation('parts');

  const visibleMaterials = useMemo(
    () => tempMaterials.filter(m => !m.deleted),
    [tempMaterials]
  );
  const uniqueCount = visibleMaterials.length;
  const totalQuantity = visibleMaterials.reduce((sum, m) => sum + m.quantity, 0);

  const forceClose = useCallback(() => {
    setIsOpenCheckoutPage(false);
    setIsOpenMaterialForm(false);
    setIsOpenQuantityEdit(false);
    setIsDiscardConfirmOpen(false);
    setIsVariousBlockOpen(false);
    setMaterialForEdit(undefined);
    onClose();
  }, [onClose]);

  const handleAddNewMaterial = useCallback(() => {
    if (visibleMaterials.some(isVariousMaterial)) {
      setIsVariousBlockOpen(true);
      return;
    }
    setMaterialForEdit(undefined);
    setIsOpenMaterialForm(true);
  }, [visibleMaterials]);

  const requestClose = useCallback(() => {
    if (isTempMaterialsDirty(tempMaterials, materials)) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    forceClose();
  }, [tempMaterials, materials, forceClose]);

  const editMaterial = useCallback((material: Material) => {
    setMaterialForEdit(material);
    if (isVariousMaterial(material)) {
      setIsOpenQuantityEdit(true);
    } else {
      setIsOpenMaterialForm(true);
    }
  }, []);

  const removeMaterial = useCallback((materialToRemove: Material) => {
    setTempMaterialsAction(
      tempMaterials.map(m =>
        String(m.id) === String(materialToRemove.id)
          ? { ...m, deleted: true, added: undefined }
          : m
      )
    );
  }, [tempMaterials, setTempMaterialsAction]);

  const handleQuantityChange = useCallback((materialId: Material['id'], value: number) => {
    setTempMaterialsAction(
      tempMaterials.map(m =>
        String(m.id) === String(materialId)
          ? { ...m, quantity: value }
          : m
      )
    );
  }, [tempMaterials, setTempMaterialsAction]);

  const onQuantitySave = useCallback((material: Material, quantity: number) => {
    setTempMaterialsAction(
      tempMaterials.map(m =>
        String(m.id) === String(material.id)
          ? { ...m, quantity }
          : m
      )
    );
  }, [tempMaterials, setTempMaterialsAction]);

  const summaryLabel = uniqueCount === 1
    ? t('reviewSummarySingular', { count: uniqueCount, qty: totalQuantity })
    : t('reviewSummaryPlural', { count: uniqueCount, qty: totalQuantity });

  return (
    <Modal className={classes['addMaterialDialog']} open={open} onClose={requestClose} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <div className={classes['partsSubHeader']}>
            <Icon name='arrow_back' onClick={requestClose}/>
            <span>{t('add')} {t('material')}</span>
          </div>
        </div>

        <div className={classes['list']}>
          {visibleMaterials.length === 0 ? (
            <p className={classes['empty']}>{t('noMaterials')}</p>
          ) : (
            visibleMaterials.map(material => {
              const isVarious = isVariousMaterial(material);
              const displayName = isVarious
                ? t('variousMaterial')
                : (material.name ?? '');
              return (
                <div className={classes['listRow']} key={String(material.id)}>
                  <div className={classes['rowContent']}>
                    <span className={classes['rowName']}>{displayName}</span>
                    <span className={classes['rowMeta']}>
                      {material.categoryName
                        ? `${material.categoryName} · `
                        : ''}
                      {formatMaterialPrice(Number(material.pricePerUnit) || 0)}
                    </span>
                  </div>
                  <div className={classes['actions']}>
                    <Quantity
                      quantity={material.quantity}
                      quantityChange={(value) => handleQuantityChange(material.id, value)}
                    />
                    {!isVarious && <MaterialRowMenu
                      material={material}
                      onEdit={editMaterial}
                      onRemove={removeMaterial}
                    />}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <button
          type="button"
          className={classes['manualAddButton']}
          onClick={handleAddNewMaterial}
        >
          + {t('addMaterial')}
        </button>

        <div className={classes['reviewFooter']}>
          <span className={classes['reviewSummary']}>{summaryLabel}</span>
          <button
            type="button"
            className={classes['reviewButton']}
            onClick={() => setIsOpenCheckoutPage(true)}
            disabled={uniqueCount === 0}
            aria-disabled={uniqueCount === 0}
          >
            {t('review')}
            <Icon name="keyboard_arrow_down" size={18} />
          </button>
        </div>
      </div>
      <MaterialForm
        open={isOpenMaterialForm}
        onClose={() => { setIsOpenMaterialForm(false); setMaterialForEdit(undefined); }}
        material={materialForEdit}
        type={type}
      />
      <QuantityEditForm
        open={isOpenQuantityEdit}
        onClose={() => { setIsOpenQuantityEdit(false); setMaterialForEdit(undefined); }}
        material={materialForEdit}
        onSave={onQuantitySave}
      />
      <Checkout open={isOpenCheckoutPage} onClose={() => setIsOpenCheckoutPage(false)} onCloseAll={forceClose} type={type} />
      <ConfirmationMenu
        isOpen={isDiscardConfirmOpen}
        onConfirm={forceClose}
        onCancel={() => setIsDiscardConfirmOpen(false)}
        confirmationText={t('discardMaterialsConfirm')}
        confirmLabel={t('discard')}
        cancelLabel={t('keepEditing')}
      />
      <TextDialog
        isOpen={isVariousBlockOpen}
        onClose={() => setIsVariousBlockOpen(false)}
        text={t('removeVariousBeforeAdd')}
      />
    </Modal>
  );
};

export default AddMaterialDialog;
